/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import { NodeType } from '../../../../backendModels/report.model';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { InputState } from '../../../../backendModels/general.model';
import {
  MEDICAL_CONDITION_PSYCHIATRY_ELEMENT_KEY,
  MedicalConditionPsychiatryRecord,
  getPsychiatryTupelsFor,
  MedicalConditionPsychiatryKeys,
} from '../../../../models/genericElements/medicalConditions/psychiatry';
import { Draft } from 'immer';
import { checkParent, uncheckChildren } from '../../../../utils/util';
import { isGenericRecordDeletable, setGenericRecordToNormal } from '../../../../models/generic';

const checkboxes1: [MedicalConditionPsychiatryKeys, string][] = getPsychiatryTupelsFor([
  MedicalConditionPsychiatryKeys.psychose,
  MedicalConditionPsychiatryKeys.angst,
  MedicalConditionPsychiatryKeys.intoxikation,
]);

const checkboxes2: [MedicalConditionPsychiatryKeys, string][] = getPsychiatryTupelsFor([
  MedicalConditionPsychiatryKeys.intoxikationAkzidentiell,
  MedicalConditionPsychiatryKeys.intoxikationAlkohol,
  MedicalConditionPsychiatryKeys.intoxikationDrogen,
  MedicalConditionPsychiatryKeys.intoxikationMedis,
  MedicalConditionPsychiatryKeys.intoxikationSonstige,
]);

const checkboxes3: [MedicalConditionPsychiatryKeys, string][] = getPsychiatryTupelsFor([
  MedicalConditionPsychiatryKeys.entzug,
  MedicalConditionPsychiatryKeys.suizid,
  MedicalConditionPsychiatryKeys.psychKrise,
]);

interface MedicalConditionPsychiatryProps {
  nodeType: NodeType;
}

export default function MedicalConditionPsychiatry({ nodeType }: MedicalConditionPsychiatryProps) {
  const { findRecordOrDefault, adaptRecord } = useContext(ReportsAPIContext);

  const record = findRecordOrDefault(
    'generic',
    nodeType,
    MEDICAL_CONDITION_PSYCHIATRY_ELEMENT_KEY
  ) as MedicalConditionPsychiatryRecord;
  const conditions = record.inputState === InputState.ENTERED ? (record.values.erkrankungenPsych?.options ?? []) : [];

  const conditionsChange = (newConditions: MedicalConditionPsychiatryKeys[]) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<MedicalConditionPsychiatryRecord>, deleteRecord) => {
        if (newConditions.length > 0) {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            erkrankungenPsych: {
              fieldType: 'multiSelect',
              options: newConditions,
            },
          };
        } else {
          delete draft.values?.erkrankungenPsych;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      MEDICAL_CONDITION_PSYCHIATRY_ELEMENT_KEY
    );
  };
  const otherChange = (value: string) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<MedicalConditionPsychiatryRecord>, deleteRecord) => {
        if (value !== '') {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            erkrankungenPsychSonstige: {
              fieldType: 'text',
              text: value,
            },
          };
        } else {
          delete draft.values?.erkrankungenPsychSonstige;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      MEDICAL_CONDITION_PSYCHIATRY_ELEMENT_KEY
    );
  };

  return (
    <DiviFieldCard
      cardType={CardId.MedicalConditionPsychiatry}
      nodeType={nodeType}
      recordType='generic'
      elementKey={MEDICAL_CONDITION_PSYCHIATRY_ELEMENT_KEY}
      normalStateButtonVisible={false}
      onNormalStateButtonClicked={() =>
        setGenericRecordToNormal(adaptRecord, nodeType, MEDICAL_CONDITION_PSYCHIATRY_ELEMENT_KEY)
      }>
      <div className='symptoms-list'>
        <CheckboxList
          items={checkboxes1}
          selectedValues={conditions}
          onValuesChange={(newConditions) =>
            conditionsChange(
              uncheckChildren(newConditions, MedicalConditionPsychiatryKeys.intoxikation, [
                MedicalConditionPsychiatryKeys.intoxikationAkzidentiell,
                MedicalConditionPsychiatryKeys.intoxikationAlkohol,
                MedicalConditionPsychiatryKeys.intoxikationDrogen,
                MedicalConditionPsychiatryKeys.intoxikationMedis,
                MedicalConditionPsychiatryKeys.intoxikationSonstige,
              ])
            )
          }
        />
        <CheckboxList
          className='indented'
          items={checkboxes2}
          selectedValues={conditions}
          onValuesChange={(newConditions) =>
            conditionsChange(
              checkParent(newConditions, MedicalConditionPsychiatryKeys.intoxikation, [
                MedicalConditionPsychiatryKeys.intoxikationAkzidentiell,
                MedicalConditionPsychiatryKeys.intoxikationAlkohol,
                MedicalConditionPsychiatryKeys.intoxikationDrogen,
                MedicalConditionPsychiatryKeys.intoxikationMedis,
                MedicalConditionPsychiatryKeys.intoxikationSonstige,
              ])
            )
          }
        />
        <CheckboxList items={checkboxes3} selectedValues={conditions} onValuesChange={conditionsChange} />
      </div>
      <DebouncedTextField
        fullWidth
        label='Sonstige...'
        value={record.inputState === InputState.ENTERED ? (record.values.erkrankungenPsychSonstige?.text ?? '') : ''}
        onDebounceChange={otherChange}
        inputProps={{ maxLength: 18 }}
      />
    </DiviFieldCard>
  );
}
